/**
 * Lite 認證與群組狀態 API
 * 註冊、check-group、check-group-status、bind-group-with-square 等
 */

import { getApiBase } from './apiUtils.js';
import type {
  ResolveContextResponse,
  BindGroupBody,
  BindGroupResponse,
  UserTenantsResponse,
  RegisterNewUserBody,
  RegisterNewUserResponse,
} from '@pos/shared/schemas';

export async function resolveContext(
  lineGroupId: string
): Promise<ResolveContextResponse> {
  const apiBase = getApiBase();
  const url = new URL(`${apiBase}/api/context/resolve`);
  url.searchParams.set('lineGroupId', lineGroupId);

  console.log('📤 解析群組綁定狀態:', { lineGroupId });

  const { apiFetch } = await import('./apiUtils.js');
  const response = await apiFetch(url.toString(), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    let errorMessage = '解析失敗，請稍後再試';
    try {
      const error = await response.json();
      console.error('❌ 解析失敗:', error);
      errorMessage = error.message || error.error || errorMessage;
    } catch {
      errorMessage = `解析失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  const result = (await response.json()) as ResolveContextResponse;
  console.log('✅ 解析成功:', result);
  return result;
}

export async function getUserTenants(
  token: string
): Promise<UserTenantsResponse> {
  const apiBase = getApiBase();
  const { apiFetch } = await import('./apiUtils.js');
  const response = await apiFetch(`${apiBase}/api/users/me/tenants`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorMessage = '取得 Tenants 失敗，請稍後再試';
    try {
      const error = await response.json();
      errorMessage = error.message || error.error || errorMessage;
    } catch {
      errorMessage = `取得 Tenants 失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  return (await response.json()) as UserTenantsResponse;
}

export async function bindGroup(
  data: BindGroupBody,
  token: string
): Promise<BindGroupResponse> {
  const apiBase = getApiBase();
  const { apiFetch } = await import('./apiUtils.js');
  const response = await apiFetch(`${apiBase}/api/context/bind`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    let errorMessage = '綁定失敗，請稍後再試';
    try {
      const error = await response.json();
      errorMessage = error.message || error.error || errorMessage;
    } catch {
      errorMessage = `綁定失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  return (await response.json()) as BindGroupResponse;
}

export async function getUserByLineIdToken(lineIdToken: string): Promise<
  UserTenantsResponse & {
    user: {
      id: string;
      email: string;
      name: string | null;
      avatarUrl: string | null;
    };
  }
> {
  const apiBase = getApiBase();
  const { apiFetch } = await import('./apiUtils.js');
  const response = await apiFetch(`${apiBase}/api/lite/auth/me`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Line-ID-Token': lineIdToken,
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      try {
        const data = await response.json();
        if (data.status === 'unknown') {
          console.log('ℹ️ 使用者不存在（新用戶，正常情況）:', data);
          throw new Error('USER_NOT_FOUND');
        }
      } catch (e) {
        if (e instanceof Error && e.message === 'USER_NOT_FOUND') throw e;
      }
    }
    if (response.status === 401) {
      let errorMessage = 'LINE ID Token 無效或已過期';
      try {
        const error = await response.json();
        errorMessage = error.message || error.error || errorMessage;
      } catch {
        // keep default
      }
      throw new Error(errorMessage);
    }
    let errorMessage = '取得使用者資訊失敗，請稍後再試';
    try {
      const error = await response.json();
      errorMessage = error.message || error.error || errorMessage;
    } catch {
      errorMessage = `取得使用者資訊失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  return (await response.json()) as UserTenantsResponse & {
    user: {
      id: string;
      email: string;
      name: string | null;
      avatarUrl: string | null;
    };
  };
}

export async function registerNewUser(
  data: RegisterNewUserBody
): Promise<RegisterNewUserResponse> {
  const apiBase = getApiBase();
  const isDevelopment =
    import.meta.env.MODE === 'development' || import.meta.env.DEV === true;

  const { apiFetch } = await import('./apiUtils.js');
  const response = await apiFetch(`${apiBase}/api/lite/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    if (isDevelopment) {
      console.warn('🧪 [開發環境] 註冊 API 失敗，使用 mock 資料');
      const mockUserId =
        import.meta.env.VITE_MOCK_LINE_USER_ID ||
        'U1234567890abcdefghijklmnopqrstuv';
      const mockDisplayName =
        import.meta.env.VITE_MOCK_LINE_DISPLAY_NAME || 'Dev Bob';
      const mockJwtToken = btoa(
        JSON.stringify({
          user_id: mockUserId,
          exp: Math.floor(Date.now() / 1000) + 86400,
        })
      );
      return {
        success: true,
        user: {
          id: mockUserId,
          email: `${mockUserId}@mock.local`,
          name: mockDisplayName,
          avatarUrl: null,
        },
        tenant: { id: 'mock-tenant-id', name: 'Mock Tenant' },
        jwtToken: `mock.${mockJwtToken}.dev`,
      };
    }
    let errorMessage = '註冊失敗，請稍後再試';
    try {
      const error = await response.json();
      errorMessage = error.message || error.error || errorMessage;
    } catch {
      errorMessage = `註冊失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  return (await response.json()) as RegisterNewUserResponse;
}

export async function registerNewUserWithTenant(data: {
  lineIdToken: string;
  tenantName: string;
  tenantType?: string | null;
  groupId: string;
}): Promise<{
  success: boolean;
  user: {
    id: string;
    email: string;
    name: string | null;
    avatarUrl: string | null;
  };
  tenant: {
    groupId: string;
    name: string;
    type: string | null;
  } | null;
  jwtToken: string;
}> {
  const apiBase = getApiBase();
  const { apiFetch } = await import('./apiUtils.js');
  const response = await apiFetch(
    `${apiBase}/api/lite/auth/register-with-tenant`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lineIdToken: data.lineIdToken,
        tenantName: data.tenantName,
        tenantType: data.tenantType || null,
        groupId: data.groupId,
      }),
    }
  );

  if (!response.ok) {
    let errorMessage = '註冊失敗，請稍後再試';
    let errorDetails: unknown = null;
    try {
      const error = await response.json();
      errorMessage = error.message || error.error || errorMessage;
      errorDetails = error;
    } catch {
      errorMessage = `註冊失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    const err = new Error(errorMessage) as Error & {
      details?: unknown;
      status?: number;
    };
    err.details = errorDetails;
    err.status = response.status;
    throw err;
  }

  return (await response.json()) as Promise<{
    success: boolean;
    user: {
      id: string;
      email: string;
      name: string | null;
      avatarUrl: string | null;
    };
    tenant: { groupId: string; name: string; type: string | null } | null;
    jwtToken: string;
  }>;
}

export async function createTenant(
  data: {
    tenant_name: string;
    slug: string;
    phone?: string | null;
    logo_url?: string | null;
  },
  token: string
): Promise<{
  success: boolean;
  data: { tenant_id: string; tenant_name: string; slug: string };
  timestamp: string;
}> {
  const apiBase = getApiBase();
  const { apiFetch } = await import('./apiUtils.js');
  const response = await apiFetch(`${apiBase}/api/auth/tenants`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      tenant_name: data.tenant_name,
      slug: data.slug,
      phone: data.phone || null,
      logo_url: data.logo_url || null,
    }),
  });

  if (!response.ok) {
    let errorMessage = '建立商店失敗，請稍後再試';
    try {
      const error = await response.json();
      errorMessage = error.error || error.message || errorMessage;
      if (response.status === 409) {
        errorMessage =
          error.error ||
          error.message ||
          '商家識別碼已被使用，請選擇其他識別碼';
      }
    } catch {
      errorMessage =
        response.status === 409
          ? '商家識別碼已被使用，請選擇其他識別碼'
          : `建立商店失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  return (await response.json()) as Promise<{
    success: boolean;
    data: { tenant_id: string; tenant_name: string; slug: string };
    timestamp: string;
  }>;
}

/** check-group API 回傳的 tenant.activityPublishConfig 形狀 */
export interface CheckGroupTenantActivityPublishConfig {
  enabled?: boolean;
  title?: string;
  content?: string;
  buttonText?: string;
  imageUrl?: string | null;
  prefix?: string;
  includeActivityTitle?: boolean;
  includeActivityDescription?: boolean;
  includeJoinUrl?: boolean;
  suffix?: string;
}

/** check-group API 回傳的 tenant.userReplyFormat 形狀 */
export interface CheckGroupTenantUserReplyFormat {
  autoReplyEnabled?: boolean;
  prefix?: string;
  operationReply?: boolean;
  remainingBelowThreshold?: number | null;
  suffix?: string;
  includeActivityUrl?: boolean;
}

export async function checkGroupTenant(
  groupId: string,
  lineIdToken?: string
): Promise<{
  hasTenant: boolean;
  tenant: {
    id: string;
    name: string;
    type: string | null;
    userId: string;
    suffix?: string | null;
    activityPublishConfig?: CheckGroupTenantActivityPublishConfig | null;
    userReplyFormat?: CheckGroupTenantUserReplyFormat | null;
  } | null;
  isOwner: boolean | null;
}> {
  const apiBase = getApiBase();
  const { apiFetch } = await import('./apiUtils.js');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (lineIdToken) headers['X-Line-ID-Token'] = lineIdToken;

  const response = await apiFetch(
    `${apiBase}/api/lite/auth/check-group?groupId=${encodeURIComponent(groupId)}`,
    { method: 'GET', headers }
  );

  if (!response.ok) {
    const isDevelopment =
      import.meta.env.MODE === 'development' || import.meta.env.DEV === true;
    if (isDevelopment) {
      return {
        hasTenant: false,
        tenant: null,
        isOwner: null,
      };
    }
    let errorMessage = '檢查群組狀態失敗，請稍後再試';
    try {
      const error = await response.json();
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      errorMessage = `檢查群組狀態失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  return (await response.json()) as Promise<{
    hasTenant: boolean;
    tenant: {
      id: string;
      name: string;
      type: string | null;
      userId: string;
      suffix?: string | null;
      activityPublishConfig?: CheckGroupTenantActivityPublishConfig | null;
      userReplyFormat?: CheckGroupTenantUserReplyFormat | null;
    } | null;
    isOwner: boolean | null;
  }>;
}

export async function checkGroupStatus(
  groupId: string,
  jwtToken?: string,
  lineIdToken?: string
): Promise<{
  status: 'NOT_REGISTERED' | 'BOUND_TO_YOU' | 'BOUND_TO_OTHER';
  tenant: {
    id: string;
    name: string;
    type: string | null;
    userId: string;
  } | null;
}> {
  const apiBase = getApiBase();
  const { apiFetch } = await import('./apiUtils.js');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
  else if (lineIdToken) headers['X-Line-ID-Token'] = lineIdToken;

  const response = await apiFetch(
    `${apiBase}/api/lite/auth/check-group-status?groupId=${encodeURIComponent(groupId)}`,
    { method: 'GET', headers }
  );

  if (!response.ok) {
    const isDevelopment =
      import.meta.env.MODE === 'development' || import.meta.env.DEV === true;
    if (isDevelopment) {
      return { status: 'NOT_REGISTERED', tenant: null };
    }
    let errorMessage = '檢查群組狀態失敗，請稍後再試';
    try {
      const error = await response.json();
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      errorMessage = `檢查群組狀態失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  return (await response.json()) as Promise<{
    status: 'NOT_REGISTERED' | 'BOUND_TO_YOU' | 'BOUND_TO_OTHER';
    tenant: {
      id: string;
      name: string;
      type: string | null;
      userId: string;
    } | null;
  }>;
}

export async function bindGroupWithSquare(
  groupId: string,
  squareId: string,
  jwtToken: string
): Promise<void> {
  const apiBase = getApiBase();
  const { apiFetch } = await import('./apiUtils.js');
  const response = await apiFetch(
    `${apiBase}/api/lite/auth/bind-group-with-square?groupId=${encodeURIComponent(groupId)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ squareId }),
    }
  );

  if (!response.ok) {
    await (
      await import('./liteApiError.js')
    ).handleLiteErrorResponse(response, {
      defaultMessage: '綁定失敗，請稍後再試',
    });
  }
}
